/**
 * SEO del micrositio público de cada restaurante.
 *
 * Reglas duras:
 * - Nunca se inventan datos: dirección, geo, cocina, rango de precios,
 *   reseñas y calificaciones NO existen en la base y por tanto NO se emiten.
 * - Sólo se indexa lo que el servidor marcó como indexable.
 * - Todo texto se sanitiza antes de llegar al <head> o al JSON-LD.
 */

export const SITE_ORIGIN = 'https://plaxter.app';
export const FALLBACK_OG_IMAGE = `${SITE_ORIGIN}/og-image-plaxter.png`;

const TITLE_MAX = 65;
const DESCRIPTION_MAX = 158;

/** Horario público mínimo tal como lo expone el payload (`hours`). */
export interface RestaurantHourInput {
  day_of_week: number | null;
  is_open?: boolean | null;
  open_time?: string | null;
  close_time?: string | null;
}

export interface RestaurantSeoInput {
  name: string;
  slug: string;
  metaTitle?: string | null;
  metaDescription?: string | null;
  metaImageUrl?: string | null;
  logoUrl?: string | null;
  isIndexable?: boolean | null;
  categoryNames?: string[];
  itemCount?: number;
  orderingActive?: boolean;
  whatsappDigits?: string | null;
  instagram?: string | null;
  facebook?: string | null;
  hours?: RestaurantHourInput[] | null;
}

export interface RestaurantSeo {
  title: string;
  description: string;
  image: string;
  canonical: string;
  robots: string;
  jsonLd: Record<string, unknown>;
  jsonLdString: string;
}

/** Texto plano de una línea, sin etiquetas ni caracteres de control. */
export function sanitizeText(raw: unknown, maxLength: number): string {
  if (typeof raw !== 'string') return '';
  const clean = raw
    .replace(/<[^>]*>/g, ' ')
    // eslint-disable-next-line no-control-regex -- limpiamos caracteres de control antes del <head>
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (clean.length <= maxLength) return clean;
  return `${clean.slice(0, maxLength - 1).trimEnd()}…`;
}

/** Sólo aceptamos URLs http(s) reales; los valores centinela se descartan. */
export function toAbsoluteHttpUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

/** Perfil social válido del dominio esperado, o null. */
export function toSocialProfileUrl(raw: unknown, host: 'instagram.com' | 'facebook.com'): string | null {
  const url = toAbsoluteHttpUrl(raw);
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
    if (hostname !== host && !hostname.endsWith(`.${host}`)) return null;
    if (parsed.pathname === '/' || parsed.pathname === '') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * URL canónica estable: sin barras extra, sin mayúsculas y sin parámetros,
 * para que una misma carta no se indexe con varias direcciones distintas.
 */
export function buildCanonicalUrl(slug: string): string {
  const clean = String(slug ?? '')
    .trim()
    .split(/[?#]/)[0]
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
  return clean ? `${SITE_ORIGIN}/${encodeURIComponent(clean)}` : SITE_ORIGIN;
}

export function buildSeoTitle(input: RestaurantSeoInput): string {
  const custom = sanitizeText(input.metaTitle, TITLE_MAX);
  if (custom) return custom;
  const name = sanitizeText(input.name, 45) || 'Restaurante';
  return sanitizeText(`${name} | Menú y pedidos`, TITLE_MAX);
}

export function buildSeoDescription(input: RestaurantSeoInput): string {
  const custom = sanitizeText(input.metaDescription, DESCRIPTION_MAX);
  if (custom) return custom;

  const name = sanitizeText(input.name, 45) || 'Este restaurante';
  const categories = (input.categoryNames ?? [])
    .map((category) => sanitizeText(category, 32))
    .filter(Boolean)
    .slice(0, 4);

  const parts: string[] = [];
  parts.push(categories.length
    ? `Menú de ${name}: ${categories.join(', ')}.`
    : `Menú de ${name}.`);
  if (input.orderingActive) parts.push('Haz tu pedido por WhatsApp.');

  return sanitizeText(parts.join(' '), DESCRIPTION_MAX);
}

/** Imagen para compartir: la personalizada, luego el logo real, luego Plaxter. */
export function resolveSeoImage(input: RestaurantSeoInput): string {
  return toAbsoluteHttpUrl(input.metaImageUrl)
    ?? toAbsoluteHttpUrl(input.logoUrl)
    ?? FALLBACK_OG_IMAGE;
}

export function buildRobots(isIndexable: boolean | null | undefined): string {
  return isIndexable === true ? 'index,follow' : 'noindex,follow';
}

/**
 * Convención del sistema (RestaurantHours.tsx / pdfGenerator.ts):
 * 0 = Domingo … 6 = Sábado.
 */
const SCHEMA_DAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;

function toSchemaTime(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const match = raw.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || hours < 0 || hours > 23) return null;
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Sólo días realmente abiertos con horas válidas; nunca se inventa un horario. */
export function buildOpeningHoursSpecification(
  hours: RestaurantHourInput[] | null | undefined,
): Array<Record<string, unknown>> {
  if (!Array.isArray(hours)) return [];
  const seen = new Set<number>();
  const spec: Array<Record<string, unknown>> = [];

  for (const hour of hours) {
    if (!hour || hour.is_open !== true) continue;
    const day = Number(hour.day_of_week);
    if (!Number.isInteger(day) || day < 0 || day > 6 || seen.has(day)) continue;
    const opens = toSchemaTime(hour.open_time);
    const closes = toSchemaTime(hour.close_time);
    if (!opens || !closes || opens === closes) continue;
    seen.add(day);
    spec.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: `https://schema.org/${SCHEMA_DAYS[day]}`,
      opens,
      closes,
    });
  }

  return spec;
}

/** JSON-LD `Restaurant` con sólo propiedades comprobadas. */
export function buildRestaurantJsonLd(input: RestaurantSeoInput): Record<string, unknown> {
  const canonical = buildCanonicalUrl(input.slug);
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: sanitizeText(input.name, 80),
    url: canonical,
    menu: canonical,
  };

  const image = toAbsoluteHttpUrl(input.metaImageUrl) ?? toAbsoluteHttpUrl(input.logoUrl);
  if (image) jsonLd.image = image;

  const digits = String(input.whatsappDigits ?? '').replace(/\D/g, '');
  if (digits.length >= 8 && digits.length <= 15) jsonLd.telephone = `+${digits}`;

  const sameAs = [
    toSocialProfileUrl(input.instagram, 'instagram.com'),
    toSocialProfileUrl(input.facebook, 'facebook.com'),
  ].filter((value): value is string => Boolean(value));
  if (sameAs.length) jsonLd.sameAs = sameAs;

  const openingHours = buildOpeningHoursSpecification(input.hours);
  if (openingHours.length) jsonLd.openingHoursSpecification = openingHours;

  return jsonLd;
}

/** Serialización segura para incrustar dentro de <script type="application/ld+json">. */
export function serializeJsonLd(jsonLd: Record<string, unknown>): string {
  return JSON.stringify(jsonLd)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

export function buildRestaurantSeo(input: RestaurantSeoInput): RestaurantSeo {
  const jsonLd = buildRestaurantJsonLd(input);
  return {
    title: buildSeoTitle(input),
    description: buildSeoDescription(input),
    image: resolveSeoImage(input),
    canonical: buildCanonicalUrl(input.slug),
    robots: buildRobots(input.isIndexable),
    jsonLd,
    jsonLdString: serializeJsonLd(jsonLd),
  };
}
